// The map hike card's "⚠ N warnings" chip opens the hike's overview page at
// its warnings section, expanded: the page is linked with #warnings, which the
// section reads on load (the anchor also scrolls it into view). And the other
// way: a warning picked in that section opens the hike's map focused on it —
// the map page is linked with ?alert=<the alert's id>, which its warnings read
// once, then take off the address.

export const WARNINGS_ANCHOR = 'warnings';

/** The hike page URL with the warnings anchor (any existing anchor replaced); null without a URL. */
export function warningsUrl(hikeUrl: string | null | undefined): string | null {
  if (typeof hikeUrl !== 'string' || !hikeUrl.trim()) return null;
  const base = hikeUrl.trim().replace(/#.*$/, '');
  return `${base}#${WARNINGS_ANCHOR}`;
}

/** Whether the page was opened to show the warnings: `#warnings` (or `?warnings=1`). */
export function shouldOpenWarnings(hash: string | null | undefined, search?: string | null): boolean {
  if ((hash || '').replace(/^#/, '') === WARNINGS_ANCHOR) return true;
  try {
    const value = new URLSearchParams(search || '').get(WARNINGS_ANCHOR);
    return value === '1' || value === 'true';
  } catch {
    return false;
  }
}

export const ALERT_PARAM = 'alert';

/** Any base for parsing a site-relative URL; never part of a result. */
const PARSE_BASE = 'https://hiiker.invalid';

/** A URL as given back: absolute when it came in absolute, site-relative otherwise. */
function serialize(url: URL, absolute: boolean): string {
  return absolute ? url.toString() : `${url.pathname}${url.search}${url.hash}`;
}

/** The hike's map focused on one of its warnings (`?alert=hazard:12`); null without a map URL or an alert. */
export function routeAlertMapUrl(mapUrl: string | null | undefined, alertId: string | null | undefined): string | null {
  if (typeof mapUrl !== 'string' || !mapUrl.trim() || !alertId) return null;
  try {
    const absolute = /^[a-z][a-z\d+.-]*:/i.test(mapUrl.trim());
    const url = new URL(mapUrl.trim(), PARSE_BASE);
    url.searchParams.set(ALERT_PARAM, alertId);
    return serialize(url, absolute);
  } catch {
    return null;
  }
}

/** The warning the map page was opened on (`?alert=…`), or null. */
export function alertIdFromSearch(search: string | null | undefined): string | null {
  try {
    const value = new URLSearchParams(search || '').get(ALERT_PARAM);
    return value && value.trim() ? value.trim() : null;
  } catch {
    return null;
  }
}

/** The address without `?alert=` (shown once; a reload or a shared link then opens the plain map). */
export function withoutAlertParam(href: string): string {
  try {
    const absolute = /^[a-z][a-z\d+.-]*:/i.test(href);
    const url = new URL(href, PARSE_BASE);
    url.searchParams.delete(ALERT_PARAM);
    return serialize(url, absolute);
  } catch {
    return href;
  }
}
